import React, { useState, useEffect, useMemo } from "react";
import {
    Ship, ClipboardPaste, Calculator, FileDown, Share2, Copy, Check, FileSpreadsheet, Trash2, Sparkles, Plus,
    Boxes, Scale, Weight, DollarSign, Package, Layers, AlertTriangle, Mail,
} from "lucide-react";
import {
    PageHeader, Button, KpiTile, ObjectStatus, TableCard, EmptyState, MessageStrip, Field,
    surface, inputCls, thCls, tdCls,
} from "./fiori";
import {
    SAMPLE_MATERIALS, CONTAINERS, BUFFER_CBM, LCL_MAX_CBM, PALLET_CBM, INCOTERMS, CURRENCIES,
    parsePaste, lineCalc, totals, byHsCode, recommend,
} from "./importExport/rules";
import { tableText, downloadExcel, downloadPdf, sharePdf, emailSummary } from "./importExport/documents";

// Imports & Exports tool (Hasbro workspace): paste materials + quantities → weights, cbm,
// value and HS codes per line → container recommendation (LCL / 20ft / 40ft) → packing
// list PDF / Excel for the customs broker → copy or share. Rules: importExport/rules.js.

const DRAFT_KEY = "hasbro-import-export-draft";
const today = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const blankDoc = () => ({ direction: "Export", reference: "", date: today(), origin: "Sydney, AU", destination: "", incoterm: "FOB", currency: "USD" });
const EXAMPLE = SAMPLE_MATERIALS.slice(0, 8).map((m, i) => `${m.code}\t${[1200, 600, 480, 960, 300, 360, 2400, 240][i]}\t${m.description}`).join("\n");
const f = (n, dp = 2) => (n == null ? "—" : Number(n).toLocaleString("en-AU", { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const int = n => (n == null ? "—" : Number(n).toLocaleString("en-AU"));
const cellInput = "w-full h-8 px-2 rounded-md border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0F161E] text-xs text-right tabular-nums text-slate-900 dark:text-slate-100 outline-none focus:border-[#0087C9]";

function loadDraft() {
    try { const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); if (d?.doc && Array.isArray(d.rows)) return d; } catch { /* no saved draft */ }
    return null;
}

export default function HasbroImportExport() {
    const draft = useMemo(loadDraft, []);
    const [doc, setDoc] = useState(draft?.doc || blankDoc());
    const [rows, setRows] = useState(draft?.rows || []);
    const [paste, setPaste] = useState("");
    const [skipped, setSkipped] = useState([]);
    const [busy, setBusy] = useState("");
    const [toast, setToast] = useState("");

    useEffect(() => {
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ doc, rows })); } catch { /* storage unavailable */ }
    }, [doc, rows]);

    const t = useMemo(() => totals(rows), [rows]);
    const rec = useMemo(() => recommend(t), [t]);
    const hs = useMemo(() => byHsCode(rows), [rows]);
    const setD = k => e => setDoc(d => ({ ...d, [k]: e.target.value }));
    const flash = msg => { setToast(msg); setTimeout(() => setToast(""), 2500); };

    const addPasted = (text = paste) => {
        const { rows: added, skipped: s } = parsePaste(text);
        setRows(r => [...r, ...added]);
        setSkipped(s);
        if (added.length) setPaste("");
        flash(added.length ? `${added.length} line${added.length === 1 ? "" : "s"} added${added.filter(r => !r.matched).length ? ` · ${added.filter(r => !r.matched).length} not in the material list — fill them in below` : ""}` : "No lines with a quantity found");
    };
    const update = (id, k, v) => setRows(rs => rs.map(r => (r.id === id ? { ...r, [k]: v } : r)));
    const numIn = (id, k, v) => update(id, k, v === "" ? null : Math.max(0, Number(v)));
    const remove = id => setRows(rs => rs.filter(r => r.id !== id));
    const clearAll = () => { if (!rows.length || window.confirm("Clear every line from the table?")) { setRows([]); setSkipped([]); } };
    const run = async (label, fn) => {
        if (!rows.length) return flash("Add some lines first");
        setBusy(label);
        try { await fn(); } catch (e) { if (e?.name !== "AbortError") flash(`Couldn't ${label.toLowerCase()} — ${e?.message || "try again"}`); }
        finally { setBusy(""); }
    };
    const copy = () => run("Copy", async () => { await navigator.clipboard.writeText(tableText(doc, rows)); flash("Table copied — paste it into Excel or an email"); });
    const share = () => run("Share", async () => {
        if (!(await sharePdf(doc, rows))) {
            await downloadPdf(doc, rows);
            window.location.href = `mailto:?subject=${encodeURIComponent(`Hasbro ${doc.direction.toLowerCase()} ${doc.reference || ""} — packing list`.trim())}&body=${encodeURIComponent(emailSummary(doc, rows))}`;
            flash("PDF downloaded — attach it to the email that just opened");
        }
    });
    const steps = [
        { icon: ClipboardPaste, label: "Paste materials", done: rows.length > 0 },
        { icon: Calculator, label: "Weights, cbm, value, HS codes", done: rows.length > 0 && !t.incomplete },
        { icon: FileDown, label: "PDF / Excel for customs", done: false },
        { icon: Share2, label: "Copy or share", done: false },
    ];

    return (
        <div className="max-w-7xl mx-auto px-1 sm:px-2 py-4 space-y-5">
            <PageHeader
                breadcrumb={[{ label: "Hasbro" }, { label: "Trade" }]}
                icon={Ship}
                title="Imports & Exports"
                subtitle="Paste a list of materials and quantities — get weights, cbm, value and HS codes, the right container, and the packing list for customs."
                actions={<>
                    <Button icon={busy === "Copy" ? Check : Copy} onClick={copy} disabled={!!busy}>Copy table</Button>
                    <Button icon={FileSpreadsheet} onClick={() => run("Excel", () => downloadExcel(doc, rows))} disabled={!!busy}>Excel</Button>
                    <Button icon={FileDown} onClick={() => run("PDF", () => downloadPdf(doc, rows))} disabled={!!busy}>{busy === "PDF" ? "Building…" : "PDF"}</Button>
                    <Button variant="emphasized" icon={Share2} onClick={share} disabled={!!busy}>Share</Button>
                </>}
            >
                <ol className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {steps.map((s, i) => (
                        <li key={s.label} className={`${surface} px-4 py-3 flex items-center gap-3`}>
                            <span className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${s.done ? "bg-emerald-500 text-white" : "bg-[#E5F4FC] text-[#0087C9] dark:bg-[#0087C9]/15 dark:text-[#6FD0F5]"}`}>
                                {s.done ? <Check size={18} /> : <s.icon size={18} />}
                            </span>
                            <span className="min-w-0">
                                <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Step {i + 1}</span>
                                <span className="block text-sm font-semibold text-[#1D2D3E] dark:text-white leading-tight">{s.label}</span>
                            </span>
                        </li>
                    ))}
                </ol>
            </PageHeader>

            <MessageStrip type="warning">
                <b>Sample material data.</b> Codes, HS codes, carton sizes, weights and values come from a sample list (SMP-…) until the Hasbro material master is connected. Check them before giving the documents to customs.
            </MessageStrip>
            {toast && <MessageStrip type="success" onClose={() => setToast("")}>{toast}</MessageStrip>}

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
                {/* Shipment details */}
                <section className={`${surface} p-5 xl:col-span-2 space-y-4`}>
                    <h2 className="text-sm font-bold text-[#1D2D3E] dark:text-white">Shipment details</h2>
                    <div className="inline-flex rounded-lg border border-slate-300 dark:border-white/10 p-0.5">
                        {["Export", "Import"].map(d => (
                            <button key={d} onClick={() => setDoc(x => ({ ...x, direction: d }))}
                                className={`px-4 h-8 rounded-md text-sm font-semibold ${doc.direction === d ? "bg-[#0087C9] text-white" : "text-slate-600 dark:text-slate-300"}`}>{d}</button>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Reference"><input value={doc.reference} onChange={setD("reference")} maxLength={60} className={inputCls} placeholder="PO / shipment no." /></Field>
                        <Field label="Date"><input type="date" value={doc.date} onChange={setD("date")} className={inputCls} /></Field>
                        <Field label="Origin"><input value={doc.origin} onChange={setD("origin")} maxLength={80} className={inputCls} placeholder="e.g. Yantian, CN" /></Field>
                        <Field label="Destination"><input value={doc.destination} onChange={setD("destination")} maxLength={80} className={inputCls} placeholder="e.g. Auckland, NZ" /></Field>
                        <Field label="Incoterm"><select value={doc.incoterm} onChange={setD("incoterm")} className={inputCls}>{INCOTERMS.map(x => <option key={x}>{x}</option>)}</select></Field>
                        <Field label="Currency"><select value={doc.currency} onChange={setD("currency")} className={inputCls}>{CURRENCIES.map(x => <option key={x}>{x}</option>)}</select></Field>
                    </div>
                </section>

                {/* Paste */}
                <section className={`${surface} p-5 xl:col-span-3 space-y-3`}>
                    <div className="flex items-center justify-between gap-2">
                        <h2 className="text-sm font-bold text-[#1D2D3E] dark:text-white">Paste materials</h2>
                        <span className="text-[11px] text-slate-500 dark:text-slate-400">Material · Qty · Description — from Excel, or one per line</span>
                    </div>
                    <textarea value={paste} onChange={e => setPaste(e.target.value)} rows={7} spellCheck={false}
                        placeholder={"SMP-1001\t1200\tMonopoly Classic board game\nSMP-3001 / 400 / Nerf Elite 2.0 blaster\nSMP-2001, 2,400, Play-Doh 10-pack"}
                        className={`${inputCls} h-auto py-2 font-mono text-xs leading-relaxed resize-y`} />
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="emphasized" icon={Plus} onClick={() => addPasted()} disabled={!paste.trim()}>Add to table</Button>
                        <Button icon={Sparkles} onClick={() => addPasted(EXAMPLE)}>Load example</Button>
                        {rows.length > 0 && <Button variant="negative" icon={Trash2} onClick={clearAll}>Clear table</Button>}
                        <span className="ml-auto text-[11px] text-slate-500 dark:text-slate-400">{SAMPLE_MATERIALS.length} materials in the sample list</span>
                    </div>
                    {skipped.length > 0 && <p className="text-xs text-amber-600 dark:text-amber-300">Skipped (no quantity): {skipped.slice(0, 5).join(" · ")}{skipped.length > 5 ? ` · +${skipped.length - 5} more` : ""}</p>}
                </section>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
                <KpiTile label="Lines" value={int(t.lines)} icon={Layers} tone="information" />
                <KpiTile label="Quantity" value={int(t.qty)} icon={Package} tone="information" />
                <KpiTile label="Cartons" value={int(t.cartons)} icon={Boxes} tone="indication" />
                <KpiTile label="CBM" value={f(t.cbm, 2)} icon={Ship} tone="information" />
                <KpiTile label="Net kg" value={f(t.netKg, 0)} icon={Scale} tone="neutral" />
                <KpiTile label="Gross kg" value={f(t.grossKg, 0)} icon={Weight} tone="neutral" />
                <KpiTile label={`Value (${doc.currency})`} value={f(t.value, 0)} icon={DollarSign} tone="positive" />
                <KpiTile label="Pallets (est.)" value={int(t.pallets)} icon={Boxes} tone="critical" hint={`${PALLET_CBM} cbm each`} />
            </div>

            {/* Container recommendation */}
            <section className={`${surface} p-5 space-y-4`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-sm font-bold text-[#1D2D3E] dark:text-white">Best way to ship it</h2>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">LCL under {LCL_MAX_CBM} cbm · 20ft up to {CONTAINERS["20GP"].loadableCbm - BUFFER_CBM} cbm · 40ft up to {CONTAINERS["40GP"].loadableCbm - BUFFER_CBM} cbm · {BUFFER_CBM} cbm buffer kept</span>
                </div>
                {rec.pick === "MIX" && (
                    <MessageStrip type="information"><b>Recommended: {rec.mixText}</b> — {rec.reason}</MessageStrip>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <OptionCard id="LCL" title="LCL — shared container" sub={`≈ ${int(t.pallets)} pallet${t.pallets === 1 ? "" : "s"}`} rec={rec} t={t} />
                    <OptionCard id="20GP" title="20ft container" sub={`${CONTAINERS["20GP"].loadableCbm} cbm loadable`} rec={rec} t={t} />
                    <OptionCard id="40GP" title="40ft container" sub={`${CONTAINERS["40GP"].loadableCbm} cbm loadable`} rec={rec} t={t} />
                </div>
                {rec.pick && rec.pick !== "MIX" && <p className="text-sm text-slate-600 dark:text-slate-300">{rec.reason}</p>}
            </section>

            {/* Lines */}
            <TableCard title="Lines" count={rows.length}
                actions={t.incomplete > 0 && <ObjectStatus tone="critical" icon={AlertTriangle}>{t.incomplete} line{t.incomplete === 1 ? "" : "s"} missing data</ObjectStatus>}>
                {!rows.length ? (
                    <EmptyState icon={ClipboardPaste} title="No materials yet"
                        text="Paste material numbers and quantities above (straight from Excel works), or load the example."
                        action={<Button variant="emphasized" icon={Sparkles} onClick={() => addPasted(EXAMPLE)}>Load example</Button>} />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>{["#", "Material", "Description", "HS code", "Qty", "Units / ctn", "Cartons", "CBM / ctn", "CBM", "Net kg / ctn", "Gross kg / ctn", "Net kg", "Gross kg", `Unit value`, `Value (${doc.currency})`, ""].map(h => <th key={h} className={`${thCls} ${["#", "Material", "Description", "HS code", ""].includes(h) ? "" : "text-right"}`}>{h}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                {rows.map((r, i) => {
                                    const c = lineCalc(r);
                                    const miss = new Set(c.missing);
                                    const warn = k => (miss.has(k) ? " border-amber-400 dark:border-amber-400/60" : "");
                                    return (
                                        <tr key={r.id} className="align-middle">
                                            <td className={`${tdCls} text-xs text-slate-400`}>{i + 1}</td>
                                            <td className={`${tdCls} whitespace-nowrap`}>
                                                <span className="font-mono text-xs font-semibold text-[#1D2D3E] dark:text-white">{r.code || "—"}</span>
                                                {!r.matched && <span className="ml-1.5 px-1.5 py-px rounded bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300 text-[10px] font-bold" title="Not in the material list — enter its details">NEW</span>}
                                            </td>
                                            <td className={`${tdCls} min-w-[200px]`}>
                                                <input value={r.description} onChange={e => update(r.id, "description", e.target.value)} maxLength={120} className={`${cellInput} text-left`} />
                                            </td>
                                            <td className={`${tdCls} min-w-[110px]`}><input value={r.hs} onChange={e => update(r.id, "hs", e.target.value.slice(0, 14))} className={`${cellInput} text-left font-mono${warn("HS code")}`} placeholder="0000.00.00" /></td>
                                            <td className={`${tdCls} w-24`}><input type="number" min="0" value={r.qty} onChange={e => numIn(r.id, "qty", e.target.value)} className={cellInput} /></td>
                                            <td className={`${tdCls} w-20`}><input type="number" min="1" value={r.unitsPerCarton ?? ""} onChange={e => numIn(r.id, "unitsPerCarton", e.target.value)} className={`${cellInput}${warn("units / carton")}`} /></td>
                                            <td className={`${tdCls} text-right tabular-nums`}>{int(c.cartons)}</td>
                                            <td className={`${tdCls} w-24`}><input type="number" min="0" step="0.001" value={r.cbmPerCarton ?? ""} onChange={e => numIn(r.id, "cbmPerCarton", e.target.value)} className={`${cellInput}${warn("cbm / carton")}`} title={r.ctnCm ? `Carton ${r.ctnCm.join(" × ")} cm` : undefined} /></td>
                                            <td className={`${tdCls} text-right tabular-nums font-semibold`}>{f(c.cbm, 3)}</td>
                                            <td className={`${tdCls} w-24`}><input type="number" min="0" step="0.01" value={r.netKgCtn ?? ""} onChange={e => numIn(r.id, "netKgCtn", e.target.value)} className={`${cellInput}${warn("net kg")}`} /></td>
                                            <td className={`${tdCls} w-24`}><input type="number" min="0" step="0.01" value={r.grossKgCtn ?? ""} onChange={e => numIn(r.id, "grossKgCtn", e.target.value)} className={`${cellInput}${warn("gross kg")}`} /></td>
                                            <td className={`${tdCls} text-right tabular-nums`}>{f(c.netKg)}</td>
                                            <td className={`${tdCls} text-right tabular-nums`}>{f(c.grossKg)}</td>
                                            <td className={`${tdCls} w-24`}><input type="number" min="0" step="0.01" value={r.unitValue ?? ""} onChange={e => numIn(r.id, "unitValue", e.target.value)} className={`${cellInput}${warn("unit value")}`} /></td>
                                            <td className={`${tdCls} text-right tabular-nums font-semibold`}>{f(c.value)}</td>
                                            <td className={`${tdCls} text-right`}><button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-500" title="Remove line"><Trash2 size={15} /></button></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-[#E5F4FC]/60 dark:bg-[#0087C9]/10 font-bold text-[#1D2D3E] dark:text-white">
                                    <td className={tdCls} colSpan={4}>Total</td>
                                    <td className={`${tdCls} text-right tabular-nums`}>{int(t.qty)}</td>
                                    <td className={tdCls} />
                                    <td className={`${tdCls} text-right tabular-nums`}>{int(t.cartons)}</td>
                                    <td className={tdCls} />
                                    <td className={`${tdCls} text-right tabular-nums`}>{f(t.cbm, 3)}</td>
                                    <td className={tdCls} colSpan={2} />
                                    <td className={`${tdCls} text-right tabular-nums`}>{f(t.netKg)}</td>
                                    <td className={`${tdCls} text-right tabular-nums`}>{f(t.grossKg)}</td>
                                    <td className={tdCls} />
                                    <td className={`${tdCls} text-right tabular-nums`}>{f(t.value)}</td>
                                    <td className={tdCls} />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </TableCard>

            {/* HS code summary */}
            {rows.length > 0 && (
                <TableCard title="Totals by HS code" count={hs.length}
                    actions={<Button variant="transparent" icon={Mail} onClick={share}>Send to broker</Button>}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr>{["HS code", "Lines", "Qty", "Cartons", "CBM", "Net kg", "Gross kg", `Value (${doc.currency})`].map((h, k) => <th key={h} className={`${thCls} ${k ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                {hs.map(g => (
                                    <tr key={g.hs}>
                                        <td className={`${tdCls} font-mono text-xs font-semibold ${g.hs.startsWith("(") ? "text-amber-600 dark:text-amber-300" : "text-[#1D2D3E] dark:text-white"}`}>{g.hs}</td>
                                        {[int(g.lines), int(g.qty), int(g.cartons), f(g.cbm, 3), f(g.netKg), f(g.grossKg), f(g.value)].map((v, k) => <td key={k} className={`${tdCls} text-right tabular-nums`}>{v}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TableCard>
            )}
        </div>
    );
}

// ── Container option card with a small to-scale picture ───────────────────────
function OptionCard({ id, title, sub, rec, t }) {
    const o = rec.options[id];
    const picked = rec.pick === id || (rec.pick === "MIX" && rec.mix.includes(id));
    const fill = o.fill == null ? null : Math.min(1, o.fill);
    const over = o.fill != null && o.fill > 1;
    return (
        <div className={`relative rounded-2xl border p-4 space-y-3 transition-colors ${picked
            ? "border-[#0087C9] bg-[#0087C9]/[0.06] dark:bg-[#0087C9]/10 ring-2 ring-[#00B7EB]/30" : "border-slate-200 dark:border-white/[0.07]"}`}>
            {picked && <span className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-[#0087C9] text-white text-[10px] font-bold uppercase tracking-wider">Recommended</span>}
            <div className="h-16 flex items-end">{id === "LCL" ? <PalletIcon count={Math.min(6, Math.max(1, t.pallets || 1))} /> : <ContainerIcon long={id === "40GP"} fill={fill} />}</div>
            <div>
                <p className="text-sm font-bold text-[#1D2D3E] dark:text-white">{title}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{sub}</p>
            </div>
            {fill != null && (
                <div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-white/[0.06] overflow-hidden">
                        <div className={`h-full rounded-full ${over ? "bg-red-500" : o.ok ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.round(fill * 100)}%` }} />
                    </div>
                    <p className={`mt-1 text-[11px] font-semibold ${over ? "text-red-600 dark:text-red-400" : "text-slate-600 dark:text-slate-300"}`}>
                        {t.cbm > 0 ? (over ? `Doesn't fit — ${Math.round(o.fill * 100)}%` : `${Math.round(o.fill * 100)}% full`) : "—"}
                    </p>
                </div>
            )}
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{o.note}</p>
        </div>
    );
}

// Side view of a container, 20ft half the length of 40ft, shaded to the fill level
function ContainerIcon({ long, fill }) {
    const w = long ? 150 : 74, h = 46;
    const ribs = Array.from({ length: long ? 18 : 9 }, (_, i) => 6 + i * ((w - 12) / (long ? 17 : 8)));
    return (
        <svg width={w} height={h + 6} viewBox={`0 0 ${w} ${h + 6}`} aria-hidden="true">
            <rect x="1" y="1" width={w - 2} height={h - 2} rx="3" className="fill-[#E5F4FC] dark:fill-[#0087C9]/15 stroke-[#0087C9]" strokeWidth="1.5" />
            {fill != null && <rect x="2" y={1 + (h - 4) * (1 - fill)} width={w - 4} height={(h - 4) * fill + 1} className="fill-[#0087C9]/30" />}
            {ribs.map(x => <line key={x} x1={x} y1="5" x2={x} y2={h - 5} className="stroke-[#0087C9]/40" strokeWidth="1" />)}
            <rect x={w - 9} y="6" width="5" height={h - 12} rx="1" className="fill-[#0087C9]/50" />
            <text x={w / 2} y={h / 2 + 4} textAnchor="middle" className="fill-[#003B6B] dark:fill-white text-[11px] font-bold">{long ? "40'" : "20'"}</text>
            <line x1="4" y1={h + 4} x2={w - 4} y2={h + 4} className="stroke-slate-300 dark:stroke-white/20" strokeWidth="2" />
        </svg>
    );
}

// A few pallets of cartons for LCL
function PalletIcon({ count }) {
    return (
        <svg width={count * 24 + 4} height="52" viewBox={`0 0 ${count * 24 + 4} 52`} aria-hidden="true">
            {Array.from({ length: count }, (_, i) => (
                <g key={i} transform={`translate(${2 + i * 24},0)`}>
                    <rect x="1" y="14" width="20" height="14" rx="1.5" className="fill-amber-200 dark:fill-amber-400/40 stroke-amber-600" strokeWidth="1" />
                    <rect x="1" y="29" width="20" height="14" rx="1.5" className="fill-amber-200 dark:fill-amber-400/40 stroke-amber-600" strokeWidth="1" />
                    <rect x="0" y="44" width="22" height="3" className="fill-amber-700" />
                    <rect x="2" y="47" width="3" height="3" className="fill-amber-700" /><rect x="9.5" y="47" width="3" height="3" className="fill-amber-700" /><rect x="17" y="47" width="3" height="3" className="fill-amber-700" />
                </g>
            ))}
        </svg>
    );
}

